import React, { useState, useEffect, useReducer, useRef } from "react";

import { isSameDay, parseISO, format } from "date-fns";
import openSocket from "../../services/socket-io";
import clsx from "clsx";
import { getBackendUrl } from "../../config";

import { green } from "@mui/material/colors";
import { makeStyles } from "@mui/styles";
import { Avatar, Button, CircularProgress, Dialog, DialogContent, DialogTitle, Divider, IconButton, InputAdornment, LinearProgress, TextField, Tooltip, Typography } from "@mui/material";
import {
  AccessTime,
  Block,
  Close,
  Done,
  DoneAll,
  ExpandMore,
  GetApp,
  History,
  KeyboardArrowDown,
  Search,
  Visibility,
  PictureAsPdf,
  InsertDriveFile,
  VideoLibrary,
  TableChart,
  Slideshow,
  Description,
  OpenInNew,
  Print,
} from "@mui/icons-material";

import MarkdownWrapper from "../MarkdownWrapper";
import VcardPreview from "../VcardPreview";
import LocationPreview from "../LocationPreview";
import ModalImageCors from "../ModalImageCors";
import QuotedMediaThumb from "../QuotedMediaThumb";
import PdfViewer from "../PdfViewer";
import quotedPreview from "../../helpers/quotedPreview";
import MessageOptionsMenu from "../MessageOptionsMenu";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import Audio from "../Audio";
import { useChatBackground } from "../../hooks/useChatBackground";
import Picker from "@emoji-mart/react";
import emojiData from "@emoji-mart/data";

const useStyles = makeStyles((theme) => ({
  messagesListWrapper: {
    overflow: "hidden",
    position: "relative",
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
  },


  messagesList: {
    backgroundImage: "none",
    backgroundColor: theme.palette.background.default,
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    padding: "20px 20px 20px 20px",
    overflowY: "scroll",
    [theme.breakpoints.down("sm")]: {
      paddingBottom: "90px",
    },
    ...theme.scrollbarStyles,
  },

  circleLoading: {
    color: green[500],
    position: "absolute",
    opacity: "70%",
    top: 0,
    left: "50%",
    marginTop: 12,
  },

  loadMoreButton: {
    alignSelf: "center",
    marginBottom: 12,
    marginTop: 4,
    textTransform: "none",
    fontSize: 13,
    color: theme.palette.text.secondary,
    borderColor: theme.palette.divider,
    "&:hover": {
      borderColor: theme.palette.text.secondary,
      backgroundColor: theme.palette.action.hover,
    },
  },

  searchBar: {
    padding: "6px 12px",
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    display: "flex",
    alignItems: "center",
    gap: 8,
  },

  searchHighlight: {
    backgroundColor: "#FFE082",
    borderRadius: 2,
    padding: "0 1px",
  },

  "@keyframes flashHighlightAnim": {
    "0%":   { boxShadow: "0 0 0 0 rgba(243,112,33,0)", filter: "brightness(1)" },
    "25%":  { boxShadow: "0 0 0 4px rgba(243,112,33,0.85)", filter: "brightness(1.12)" },
    "50%":  { boxShadow: "0 0 0 4px rgba(243,112,33,0.35)", filter: "brightness(1)" },
    "75%":  { boxShadow: "0 0 0 4px rgba(243,112,33,0.85)", filter: "brightness(1.12)" },
    "100%": { boxShadow: "0 0 0 0 rgba(243,112,33,0)", filter: "brightness(1)" },
  },
  flashHighlight: {
    animation: "$flashHighlightAnim 1.6s ease-in-out 1",
    borderRadius: 8,
  },
  quotedClickable: {
    cursor: "pointer",
    transition: "filter 0.15s",
    "&:hover": { filter: "brightness(0.96)" },
  },

  reactionPickerWrapper: {
    position: "absolute",
    bottom: "calc(100% + 4px)",
    zIndex: 10,
    boxShadow: "0 4px 20px rgba(0,0,0,0.25)",
    borderRadius: 12,
    overflow: "hidden",
  },

  reactionPickerWrapperLeft: {
    left: 0,
  },

  reactionPickerWrapperRight: {
    right: 0,
  },

  reactionButton: {
    display: "none",
    position: "absolute",
    bottom: 0,
    fontSize: 16,
    padding: 2,
    color: theme.palette.text.secondary,
    cursor: "pointer",
    lineHeight: 1,
    userSelect: "none",
  },

  reactionButtonLeft: {
    right: -24,
  },

  reactionButtonRight: {
    left: -24,
  },

  messageLeft: {
    marginRight: 20,
    marginTop: 2,
    minWidth: 100,
    maxWidth: 600,
    height: "auto",
    display: "block",
    position: "relative",
    "&:hover #messageActionsButton": {
      display: "flex",
    },
    "&:hover $reactionButton": {
      display: "block",
    },

    whiteSpace: "pre-wrap",
    backgroundColor: theme.custom.chat.receivedBg,
    color: theme.custom.chat.receivedText,
    border: `1px solid ${theme.palette.divider}`,
    alignSelf: "flex-start",
    borderTopLeftRadius: 0,
    borderTopRightRadius: 12,
    borderBottomLeftRadius: 12,
    borderBottomRightRadius: 12,
    paddingLeft: 5,
    paddingRight: 5,
    paddingTop: 5,
    paddingBottom: 0,
    boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
  },

  quotedContainerLeft: {
    margin: "-3px -80px 6px -6px",
    overflow: "hidden",
    backgroundColor: theme.custom.chat.quotedReceivedBg,
    borderRadius: "7.5px",
    display: "flex",
    position: "relative",
  },

  quotedMsg: {
    padding: 10,
    maxWidth: 300,
    height: "auto",
    display: "block",
    whiteSpace: "pre-wrap",
    overflow: "hidden",
  },

  quotedPreviewLine: {
    marginTop: 3,
    fontSize: "0.98em",
    lineHeight: 1.35,
    color: theme.palette.text.secondary,
    display: "-webkit-box",
    WebkitLineClamp: 3,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    wordBreak: "break-word",
    whiteSpace: "normal",
  },

  quotedSideColorLeft: {
    flex: "none",
    width: "4px",
    backgroundColor: theme.custom.chat.contactAccent,
  },

  messageRight: {
    marginLeft: 20,
    marginTop: 2,
    minWidth: 100,
    maxWidth: 600,
    height: "auto",
    display: "block",
    position: "relative",
    "&:hover #messageActionsButton": {
      display: "flex",
    },
    "&:hover $reactionButton": {
      display: "block",
    },

    whiteSpace: "pre-wrap",
    backgroundColor: theme.custom.chat.sentBg,
    color: theme.custom.chat.sentText,
    alignSelf: "flex-end",
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
    borderBottomLeftRadius: 12,
    borderBottomRightRadius: 0,
    paddingLeft: 5,
    paddingRight: 5,
    paddingTop: 5,
    paddingBottom: 0,
    boxShadow: "0 1px 3px rgba(243,112,33,0.12)",
  },

  quotedContainerRight: {
    margin: "-3px -80px 6px -6px",
    overflowY: "hidden",
    backgroundColor: theme.custom.chat.quotedSentBg,
    borderRadius: "7.5px",
    display: "flex",
    position: "relative",
  },

  quotedMsgRight: {
    padding: 10,
    maxWidth: 300,
    height: "auto",
    whiteSpace: "pre-wrap",
  },

  quotedSideColorRight: {
    flex: "none",
    width: "4px",
    backgroundColor: theme.custom.chat.accent,
  },

  messageActionsButton: {
    display: "none",
    color: "#999",
    zIndex: 1,
    backgroundColor: "inherit",
    "&:hover, &.Mui-focusVisible": { backgroundColor: "inherit" },
  },

  messageContactName: {
    display: "flex",
    color: theme.custom.chat.contactAccent,
    fontWeight: 500,
  },

  textContentItem: {
    overflowWrap: "break-word",
    padding: "3px 80px 6px 6px",
  },

  textContentItemDeleted: {
    fontStyle: "italic",
    color: theme.palette.text.disabled,
    overflowWrap: "break-word",
    padding: "3px 80px 6px 6px",
  },

  messageMedia: {
    objectFit: "cover",
    width: 250,
    height: 200,
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
  },

  timestamp: {
    fontSize: 11,
    position: "absolute",
    bottom: 0,
    right: 5,
    color: theme.palette.text.secondary,
  },

  dailyTimestamp: {
    alignItems: "center",
    textAlign: "center",
    alignSelf: "center",
    width: "110px",
    backgroundColor: theme.palette.divider,
    margin: "10px",
    borderRadius: "10px",
    boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
  },

  dailyTimestampText: {
    color: theme.palette.text.secondary,
    padding: 8,
    alignSelf: "center",
    marginLeft: "0px",
  },

  ackIcons: {
    fontSize: 18,
    verticalAlign: "middle",
    marginLeft: 4,
  },

  deletedIcon: {
    fontSize: 18,
    verticalAlign: "middle",
    marginRight: 4,
  },

  ackDoneAllIcon: {
    color: green[500],
    fontSize: 18,
    verticalAlign: "middle",
    marginLeft: 4,
  },

  reactionsContainer: {
    display: "flex",
    flexWrap: "wrap",
    gap: 2,
    marginTop: 2,
    paddingLeft: 4,
  },

  reactionChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 2,
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 12,
    padding: "1px 6px",
    fontSize: 14,
    lineHeight: 1.4,
    cursor: "default",
    boxShadow: "0 1px 2px rgba(0,0,0,0.12)",
    "& small": {
      fontSize: 11,
      color: theme.palette.text.secondary,
      marginLeft: 2,
    },
  },

  downloadMedia: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "inherit",
    padding: 10,
  },

  videoContainer: {
    position: "relative",
    display: "inline-block",
    borderRadius: 8,
    overflow: "hidden",
  },

  videoFilename: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    padding: "4px 6px 2px",
    textOverflow: "ellipsis",
    overflow: "hidden",
    whiteSpace: "nowrap",
    maxWidth: 250,
  },

  pdfContainer: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
    padding: "8px 10px",
    minWidth: 220,
  },

  pdfHeader: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },

  pdfIcon: {
    color: "#e53935",
    fontSize: 32,
    flexShrink: 0,
  },

  pdfInfo: {
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },

  pdfFilename: {
    fontSize: "0.8rem",
    fontWeight: 500,
    textOverflow: "ellipsis",
    overflow: "hidden",
    whiteSpace: "nowrap",
    maxWidth: 160,
  },

  pdfSubtitle: {
    fontSize: "0.7rem",
    color: theme.palette.text.secondary,
  },

  pdfActions: {
    display: "flex",
    gap: 6,
  },

  docContainer: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "8px 10px",
    minWidth: 220,
  },

  docIcon: {
    fontSize: 36,
    flexShrink: 0,
  },

  docInfo: {
    flexGrow: 1,
    overflow: "hidden",
  },

  docFilename: {
    fontSize: "0.8rem",
    fontWeight: 500,
    textOverflow: "ellipsis",
    overflow: "hidden",
    whiteSpace: "nowrap",
    maxWidth: 180,
  },

  docMime: {
    fontSize: "0.7rem",
    color: theme.palette.text.secondary,
  },

  messagePrivate: {
    alignSelf: "center",
    marginTop: 4,
    marginBottom: 4,
    maxWidth: "70%",
    backgroundColor: theme.palette.mode === "dark" ? "#2A2200" : "#FFF9C4",
    border: "1px dashed #F9A825",
    borderRadius: 8,
    padding: "5px 12px",
    color: theme.palette.mode === "dark" ? "#E8C070" : "#5D4037",
    fontSize: 12,
    fontStyle: "italic",
    textAlign: "center",
    display: "flex",
    alignItems: "center",
    gap: 4,
  },

  privateTimestamp: {
    fontSize: 10,
    color: theme.palette.mode === "dark" ? "#A07850" : "#8A7260",
    marginLeft: 8,
    whiteSpace: "nowrap",
  },

  privateAuthor: {
    fontWeight: 600,
    fontStyle: "normal",
    color: theme.palette.mode === "dark" ? "#F0D080" : "#6D4C41",
    fontSize: 11,
    marginRight: 4,
  },

  syncHistoryBar: {
    alignSelf: "center",
    width: "100%",
    display: "flex",
    justifyContent: "center",
    padding: "6px 0 10px",
  },

  syncHistoryButton: {
    textTransform: "none",
    fontSize: 12,
    color: theme.palette.text.secondary,
  },

  syncProgressWrap: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    width: "60%",
    maxWidth: 320,
    gap: 4,
  },

  syncProgressText: {
    fontSize: 12,
    color: theme.palette.text.secondary,
  },

  syncDoneText: {
    fontSize: 12,
    color: green[600],
  },

  syncErrorButton: {
    textTransform: "none",
    fontSize: 12,
    color: "#e53935",
  },
}));

const reducer = (state, action) => {
  if (action.type === "LOAD_MESSAGES") {
    const messages = action.payload || [];
    const newMessages = [];

    messages.forEach((message) => {
      const messageIndex = state.findIndex((m) => m.id === message.id);
      if (messageIndex !== -1) {
        state[messageIndex] = message;
      } else {
        newMessages.push(message);
      }
    });

    return [...newMessages, ...state];
  }

  if (action.type === "ADD_MESSAGE") {
    const newMessage = action.payload;
    const messageIndex = state.findIndex((m) => m.id === newMessage.id);

    if (messageIndex !== -1) {
      state[messageIndex] = newMessage;
    } else {
      state.push(newMessage);
      state.sort((a, b) => {
        const ta = a.createdAt ? new Date(a.createdAt).getTime() : (a.timestamp || 0) * 1000;
        const tb = b.createdAt ? new Date(b.createdAt).getTime() : (b.timestamp || 0) * 1000;
        if (ta !== tb) return ta - tb;
        return a.id < b.id ? -1 : 1;
      });
    }

    return [...state];
  }

  if (action.type === "UPDATE_MESSAGE") {
    const messageToUpdate = action.payload;
    const messageIndex = state.findIndex((m) => m.id === messageToUpdate.id);

    if (messageIndex !== -1) {
      // Merge em vez de substituir: vários emits de "update" (reação, edição,
      // apagar, enquete) chegam sem os includes quotedMsg/contact — substituir
      // apagava o preview da citação e o nome do autor da tela.
      state[messageIndex] = { ...state[messageIndex], ...messageToUpdate };
    }

    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const getMediaUrl = (mediaUrl) => {
  if (!mediaUrl) return null;
  if (/^(https?:|blob:|data:)/.test(mediaUrl)) return mediaUrl;
  const base = (getBackendUrl() || "").replace(/\/$/, "");
  return `${base}/public/${encodeURIComponent(mediaUrl)}`;
};

// /public exige token JWT (publicMediaAuth) — um <a href download> ou <a target>
// cru não manda o token e resulta em 401 ("não faz nada"). Buscamos o arquivo
// via `api` (Authorization header) como blob e disparamos o download/abertura.
const fetchMediaBlob = async (mediaUrl) => {
  const src = /^(https?:|blob:|data:)/.test(mediaUrl) ? mediaUrl : `/public/${encodeURIComponent(mediaUrl)}`;
  const { data, headers } = await api.get(src, { responseType: "blob" });
  return window.URL.createObjectURL(new Blob([data], { type: headers["content-type"] }));
};

const downloadMediaFile = async (mediaUrl, filename) => {
  try {
    const blobUrl = await fetchMediaBlob(mediaUrl);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = filename || "arquivo";
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.setTimeout(() => window.URL.revokeObjectURL(blobUrl), 10000);
  } catch (err) {
    toastError(err);
  }
};

// Documentos que o backend converte para PDF (doc/ppt/xls...). PDF vai direto.
const isConvertibleDoc = (name) =>
  /\.(docx?|pptx?|xlsx?|odt|ods|odp|rtf|txt|csv)$/i.test(name || "");

// Busca o documento como PDF pronto (converte no backend se não for PDF).
// PDF vai direto, intacto: reescrevê-lo para embutir fontes foi testado e piora
// — o pdf.js do Firefox substitui a fonte ausente por uma MAIS LEVE que a Arial.
const fetchPdfBlob = async (mediaUrl, isPdf) => {
  const src = isPdf
    ? (/^(https?:|blob:|data:)/.test(mediaUrl) ? mediaUrl : `/public/${encodeURIComponent(mediaUrl)}`)
    : `/media-pdf/${encodeURIComponent(mediaUrl)}`;
  const { data } = await api.get(src, { responseType: "blob" });
  return window.URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
};

// Impressão rápida: abre o PDF num iframe oculto e dispara o diálogo de impressão.
const printAsPdf = async (mediaUrl, isPdf) => {
  if (!isPdf) toast.info("Preparando documento para impressão…", { autoClose: 2500 });
  try {
    const blobUrl = await fetchPdfBlob(mediaUrl, isPdf);
    const iframe = document.createElement("iframe");
    iframe.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;";
    iframe.src = blobUrl;
    iframe.onload = () => {
      try {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
      } catch {
        window.open(blobUrl, "_blank");
      }
      window.setTimeout(() => {
        iframe.remove();
        window.URL.revokeObjectURL(blobUrl);
      }, 60000);
    };
    document.body.appendChild(iframe);
  } catch (err) {
    toastError(err);
  }
};

const MessagesList = ({ ticketId, isGroup }) => {
  const classes = useStyles();
  const { bg } = useChatBackground();

  const [messagesList, dispatch] = useReducer(reducer, []);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const lastMessageRef = useRef();
  const messagesListRef = useRef();
  const isAtBottomRef = useRef(true);
  const needsScrollRef = useRef(false);
  const [showScrollBtn, setShowScrollBtn] = useState(false);

  const [selectedMessage, setSelectedMessage] = useState({});
  const [anchorEl, setAnchorEl] = useState(null);
  const messageOptionsMenuOpen = Boolean(anchorEl);
  const currentTicketId = useRef(ticketId);

  const [syncStatus, setSyncStatus] = useState("idle"); // idle | fetching | syncing | done | error
  const [syncProgress, setSyncProgress] = useState({ current: 0, total: 0 });
  const [reloadKey, setReloadKey] = useState(0);
  const [reactionPickerMsgId, setReactionPickerMsgId] = useState(null);
  const [highlightedId, setHighlightedId] = useState(null);
  const [reactionsDetail, setReactionsDetail] = useState(null); // { reactions } do chip clicado
  const [pdfView, setPdfView] = useState(null); // { loading?, blobUrl?, filename } do visualizador embutido

  // Marcador "impresso" por arquivo (localStorage) — para não se perder ao imprimir
  // muitos arquivos 1 por 1. Persiste no navegador; clicável para desmarcar.
  const [printedIds, setPrintedIds] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem("kr_printed_media") || "[]")); }
    catch { return new Set(); }
  });
  const persistPrinted = (set) => {
    try { localStorage.setItem("kr_printed_media", JSON.stringify([...set])); } catch { /* quota/priv */ }
  };
  const togglePrinted = (id) => {
    setPrintedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      persistPrinted(next);
      return next;
    });
  };
  const handlePrint = (message, isPdf) => {
    printAsPdf(message.mediaUrl, isPdf);
    setPrintedIds(prev => {
      if (prev.has(message.id)) return prev;
      const next = new Set(prev); next.add(message.id);
      persistPrinted(next);
      return next;
    });
  };
  const PrintedBadge = ({ id }) => printedIds.has(id) ? (
    <span
      onClick={() => togglePrinted(id)}
      title="Impresso — clique para desmarcar"
      style={{
        display: "inline-flex", alignItems: "center", gap: 3, cursor: "pointer",
        background: "#e8f5e9", color: "#2e7d32", border: "1px solid #a5d6a7",
        borderRadius: 10, padding: "1px 8px", fontSize: "0.68rem", fontWeight: 700, whiteSpace: "nowrap",
      }}
    >
      ✓ Impresso
    </span>
  ) : null;

  // Abre o visualizador de PDF embutido (PDF direto; doc/ppt/xls converte no backend).
  const openPdfView = async (mediaUrl, isPdf, filename) => {
    setPdfView({ loading: true, filename });
    try {
      const blobUrl = await fetchPdfBlob(mediaUrl, isPdf);
      setPdfView({ blobUrl, filename });
    } catch (err) {
      setPdfView(null);
      toastError(err);
    }
  };
  const closePdfView = () => {
    setPdfView((cur) => {
      if (cur?.blobUrl) window.URL.revokeObjectURL(cur.blobUrl);
      return null;
    });
  };

  // Rola até a mensagem original (resposta/citação) e pisca um destaque colorido.
  const scrollToMessage = (msgId) => {
    if (!msgId) return;
    const el = document.getElementById(`msg-${msgId}`);
    if (!el) {
      // original fora da lista carregada (página antiga ainda não buscada)
      toast.info("A mensagem original é antiga e ainda não está carregada. Role para cima para carregá-la.");
      return;
    }
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    setHighlightedId(msgId);
    window.setTimeout(() => setHighlightedId((cur) => (cur === msgId ? null : cur)), 1600);
  };


  // Rótulo amigável de quem reagiu (agent_X = atendente; com @ = cliente).
  const reactionSenderLabel = (sender) => {
    if (typeof sender === "string" && sender.startsWith("agent_")) return "Atendente";
    return "Cliente";
  };

  const [searchOpen, setSearchOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const searchRef = useRef();

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
    setSyncStatus("idle");
    setSyncProgress({ current: 0, total: 0 });
    setReloadKey(0);
    currentTicketId.current = ticketId;
    isAtBottomRef.current = true;
    needsScrollRef.current = false;
  }, [ticketId]);

  useEffect(() => {
    if (needsScrollRef.current && messagesList.length > 0) {
      scrollToBottom();
      needsScrollRef.current = false;
    }
  }, [messagesList]);

  useEffect(() => {
    if (syncStatus !== "done" && syncStatus !== "error") return;
    const t = setTimeout(() => setSyncStatus("idle"), 5000);
    return () => clearTimeout(t);
  }, [syncStatus]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchMessages = async () => {
        try {
          const { data } = await api.get("/messages/" + ticketId, {
            params: { pageNumber },
          });

          setLoading(false);
          if (currentTicketId.current === ticketId) {
            dispatch({ type: "LOAD_MESSAGES", payload: data.messages });
            setHasMore(data.hasMore);
            if (pageNumber === 1 && data.messages.length > 1) needsScrollRef.current = true;
          }
        } catch (err) {
          setLoading(false);
          toastError(err);
        }
      };
      fetchMessages();
    }, 500);
    return () => {
      clearTimeout(delayDebounceFn);
    };
  }, [pageNumber, ticketId, reloadKey]);

  useEffect(() => {
    const socket = openSocket();

    const ticketIdInt = parseInt(ticketId);

    const handleConnect = () => socket.emit("joinChatBox", ticketId);

    const handleAppMessage = (data) => {
      if (data.message?.ticketId !== ticketIdInt) return;
      if (data.action === "create") {
        dispatch({ type: "ADD_MESSAGE", payload: data.message });
        if (isAtBottomRef.current) scrollToBottom();
        else setShowScrollBtn(true);
      }
      if (data.action === "update") {
        dispatch({ type: "UPDATE_MESSAGE", payload: data.message });
      }
    };

    const handleSyncProgress = (data) => {
      if (data.ticketId !== ticketIdInt) return;
      setSyncStatus(data.status);
      if (data.current !== undefined) {
        setSyncProgress({ current: data.current, total: data.total });
      }
      if (data.status === "done") {
        dispatch({ type: "RESET" });
        setPageNumber(1);
        setReloadKey(k => k + 1);
      }
    };

    socket.emit("joinChatBox", ticketId);
    socket.on("connect", handleConnect);
    socket.on("appMessage", handleAppMessage);
    socket.on("syncProgress", handleSyncProgress);

    return () => {
      socket.emit("leaveChatBox", ticketId);
      socket.off("connect", handleConnect);
      socket.off("appMessage", handleAppMessage);
      socket.off("syncProgress", handleSyncProgress);
      socket.disconnect();
    };
  }, [ticketId]);

  const handleSyncHistory = async () => {
    setSyncStatus("fetching");
    setSyncProgress({ current: 0, total: 0 });
    try {
      await api.post(`/messages/${ticketId}/sync`);
    } catch {
      setSyncStatus("error");
    }
  };

  const loadMore = () => {
    setPageNumber((prevPageNumber) => prevPageNumber + 1);
  };

  const scrollToBottom = (smooth = false) => {
    if (lastMessageRef.current) {
      lastMessageRef.current.scrollIntoView({ behavior: smooth ? "smooth" : "auto" });
    }
    isAtBottomRef.current = true;
    setShowScrollBtn(false);
  };

  const handleScroll = () => {
    const el = messagesListRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    const atBottom = distanceFromBottom < 150;
    if (atBottom !== isAtBottomRef.current) {
      isAtBottomRef.current = atBottom;
      setShowScrollBtn(!atBottom);
    }
  };

  const handleOpenMessageOptionsMenu = (e, message) => {
    setAnchorEl(e.currentTarget);
    setSelectedMessage(message);
  };

  const handleCloseMessageOptionsMenu = (e) => {
    setAnchorEl(null);
  };

  // Fecha o picker de reação ao clicar fora
  useEffect(() => {
    if (!reactionPickerMsgId) return;
    const close = () => setReactionPickerMsgId(null);
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [reactionPickerMsgId]);

  const handleReact = async (messageId, emoji) => {
    setReactionPickerMsgId(null);
    try {
      await api.post(`/messages/${messageId}/react`, { emoji });
    } catch (err) {
      toastError(err);
    }
  };

  const getFilenameFromMessage = (message) => {
    if (message.body && !/^\s*$/.test(message.body)) {
      const candidate = message.body.split("\n")[0].trim();
      if (candidate.includes(".") && !candidate.includes(" ") && candidate.length < 120) {
        return candidate;
      }
    }
    if (message.mediaUrl) {
      const raw = message.mediaUrl.split("/").pop().split("?")[0];
      // O backend manda o nome com encodeURIComponent (nomes com "%", "#", espaço…).
      if (raw) {
        try { return decodeURIComponent(raw); } catch { return raw; }
      }
    }
    return "arquivo";
  };

  const getDocIconComponent = (filename) => {
    const ext = (filename.split(".").pop() || "").toLowerCase();
    if (["pdf"].includes(ext)) return <PictureAsPdf style={{ color: "#e53935", fontSize: 36 }} />;
    if (["doc", "docx", "odt", "txt", "rtf"].includes(ext)) return <Description style={{ color: "#1565c0", fontSize: 36 }} />;
    if (["xls", "xlsx", "csv", "ods"].includes(ext)) return <TableChart style={{ color: "#2e7d32", fontSize: 36 }} />;
    if (["ppt", "pptx", "odp"].includes(ext)) return <Slideshow style={{ color: "#e65100", fontSize: 36 }} />;
    if (["mp4", "webm", "ogg", "mov", "avi"].includes(ext)) return <VideoLibrary style={{ color: "#6a1b9a", fontSize: 36 }} />;
    return <InsertDriveFile style={{ color: "#546e7a", fontSize: 36 }} />;
  };

  const checkMessageMedia = (message) => {
    if (message.mediaType === "location" && message.body.split('|').length >= 2) {
      let locationParts = message.body.split('|')
      let imageLocation = locationParts[0]
      let linkLocation = locationParts[1]

      let descriptionLocation = null

      if (locationParts.length > 2)
        descriptionLocation = message.body.split('|')[2]

      return <LocationPreview image={imageLocation} link={linkLocation} description={descriptionLocation} />
    }
    else if (message.mediaType === "vcard") {
      let array = message.body.split("\n");
      let obj = [];
      let contact = "";
      for (let index = 0; index < array.length; index++) {
        const v = array[index];
        let values = v.split(":");
        for (let ind = 0; ind < values.length; ind++) {
          if (values[ind].indexOf("+") !== -1) {
            obj.push({ number: values[ind] });
          }
          if (values[ind].indexOf("FN") !== -1) {
            contact = values[ind + 1];
          }
        }
      }
      return <VcardPreview contact={contact} numbers={obj[0]?.number} />
    }
    else if (message.mediaType === "image" || message.mediaType === "sticker") {
      return <ModalImageCors imageUrl={getMediaUrl(message.mediaUrl)} />;
    } else if (message.mediaType === "audio") {
      return <Audio url={getMediaUrl(message.mediaUrl)} messageId={message.id} fromMe={message.fromMe} transcription={message.transcription} />;
    } else if (message.mediaType === "video") {
      const filename = getFilenameFromMessage(message);
      const url = getMediaUrl(message.mediaUrl);
      return (
        <div className={classes.videoContainer}>
          <video
            className={classes.messageMedia}
            src={url}
            controls
            preload="metadata"
          />
          <div className={classes.videoFilename}>{filename}</div>
          <div style={{ display: "flex", justifyContent: "flex-end", padding: "0 6px 4px" }}>
            <Button
              size="small"
              startIcon={<GetApp />}
              onClick={() => downloadMediaFile(message.mediaUrl, filename)}
              style={{ fontSize: "0.7rem", textTransform: "none" }}
            >
              Baixar
            </Button>
          </div>
        </div>
      );
    } else if (
      message.mediaType === "application" ||
      (message.mediaUrl && /\.(pdf|doc|docx|xls|xlsx|ppt|pptx|csv|txt|odt|ods|odp|rtf)$/i.test(message.mediaUrl))
    ) {
      const filename = getFilenameFromMessage(message);
      const isPdf = /\.pdf$/i.test(filename) || /\.pdf$/i.test(message.mediaUrl || "");

      if (isPdf) {
        return (
          <>
            <div className={classes.pdfContainer}>
              <div className={classes.pdfHeader}>
                <PictureAsPdf className={classes.pdfIcon} />
                <div className={classes.pdfInfo}>
                  <span className={classes.pdfFilename}>{filename}</span>
                  <span className={classes.pdfSubtitle}>PDF</span>
                </div>
                <PrintedBadge id={message.id} />
              </div>
              <div className={classes.pdfActions}>
                <Button
                  size="small"
                  startIcon={<OpenInNew />}
                  onClick={() => openPdfView(message.mediaUrl, true, filename)}
                  variant="outlined"
                  color="primary"
                  style={{ fontSize: "0.7rem", textTransform: "none" }}
                >
                  Abrir PDF
                </Button>
                <Button
                  size="small"
                  startIcon={<Print />}
                  onClick={() => handlePrint(message, true)}
                  style={{ fontSize: "0.7rem", textTransform: "none" }}
                >
                  Imprimir
                </Button>
                <Button
                  size="small"
                  startIcon={<GetApp />}
                  onClick={() => downloadMediaFile(message.mediaUrl, filename)}
                  style={{ fontSize: "0.7rem", textTransform: "none" }}
                >
                  Baixar
                </Button>
              </div>
            </div>
            <Divider />
          </>
        );
      }

      return (
        <>
          <div className={classes.docContainer}>
            {getDocIconComponent(filename)}
            <div className={classes.docInfo}>
              <div className={classes.docFilename}>{filename}</div>
              <div className={classes.docMime}>
                {(filename.split(".").pop() || "arquivo").toUpperCase()}
                <PrintedBadge id={message.id} />
              </div>
            </div>
            <div style={{ display: "flex", gap: 2, flexShrink: 0 }}>
              {isConvertibleDoc(filename) && (
                <>
                  <Button
                    size="small"
                    onClick={() => openPdfView(message.mediaUrl, false, filename)}
                    title="Visualizar"
                    style={{ minWidth: 0, padding: 6 }}
                  >
                    <OpenInNew fontSize="small" />
                  </Button>
                  <Button
                    size="small"
                    onClick={() => handlePrint(message, false)}
                    title="Imprimir"
                    style={{ minWidth: 0, padding: 6 }}
                  >
                    <Print fontSize="small" />
                  </Button>
                </>
              )}
              <Button
                size="small"
                onClick={() => downloadMediaFile(message.mediaUrl, filename)}
                title="Baixar"
                style={{ minWidth: 0, padding: 6 }}
              >
                <GetApp fontSize="small" />
              </Button>
            </div>
          </div>
          <Divider />
        </>
      );
    } else {
      const filename = getFilenameFromMessage(message);
      return (
        <>
          <div className={classes.docContainer}>
            {getDocIconComponent(filename)}
            <div className={classes.docInfo}>
              <div className={classes.docFilename}>{filename}</div>
            </div>
            <Button
              size="small"
              onClick={() => downloadMediaFile(message.mediaUrl, filename)}
              title="Baixar"
              style={{ minWidth: 0, padding: 6 }}
            >
              <GetApp fontSize="small" />
            </Button>
          </div>
          <Divider />
        </>
      );
    }
  };

  const renderMessageAck = (message) => {
    if (message.ack === 0) {
      return <AccessTime fontSize="small" className={classes.ackIcons} />;
    }
    if (message.ack === 1) {
      return <Done fontSize="small" className={classes.ackIcons} />;
    }
    if (message.ack === 2) {
      return <DoneAll fontSize="small" className={classes.ackIcons} />;
    }
    if (message.ack === 3 || message.ack === 4) {
      return <DoneAll fontSize="small" className={classes.ackDoneAllIcon} />;
    }
  };

  const renderReactions = (message) => {
    let reactions = message.reactions;
    if (!reactions) return null;
    if (typeof reactions === "string") {
      try { reactions = JSON.parse(reactions); } catch { return null; }
    }
    if (typeof reactions !== "object" || Array.isArray(reactions) || Object.keys(reactions).length === 0) return null;
    const reactionsObj = reactions;
    return (
      <div className={classes.reactionsContainer}>
        {Object.entries(reactionsObj).map(([emoji, senders]) => (
          <Tooltip
            key={emoji}
            title="Ver quem reagiu"
            arrow
          >
            <span
              className={classes.reactionChip}
              onClick={() => setReactionsDetail({ reactions: reactionsObj })}
            >
              {emoji}
              {senders.length > 1 && <small>{senders.length}</small>}
            </span>
          </Tooltip>
        ))}
      </div>
    );
  };

  const renderPollVotes = (message) => {
    if (message.mediaType !== "poll" && !message.votes) return null;
    let votes = message.votes;
    if (!votes) return null;
    if (typeof votes === "string") {
      try { votes = JSON.parse(votes); } catch { return null; }
    }
    if (!votes || typeof votes !== "object") return null;
    const entries = Object.entries(votes);
    if (!entries.length) return null;
    const total = entries.reduce((s, [, v]) => s + (Array.isArray(v) ? v.length : 0), 0);
    return (
      <div style={{ marginTop: 6, fontSize: 12, color: "inherit" }}>
        {entries.map(([option, voters]) => {
          const count = Array.isArray(voters) ? voters.length : 0;
          const pct = total > 0 ? Math.round((count / total) * 100) : 0;
          return (
            <div key={option} style={{ marginBottom: 4 }}>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span>{option}</span><span>{count} ({pct}%)</span>
              </div>
              <div style={{ height: 4, borderRadius: 2, background: "rgba(0,0,0,0.15)", overflow: "hidden" }}>
                <div style={{ height: "100%", width: `${pct}%`, background: "currentColor", opacity: 0.5 }} />
              </div>
            </div>
          );
        })}
        <div style={{ marginTop: 4, opacity: 0.6 }}>{total} voto{total !== 1 ? "s" : ""}</div>
      </div>
    );
  };

  // Selos no topo do balão: encaminhada / enviada por dispositivo externo.
  const renderMsgFlags = (message) => (
    <>
      {message.isForwarded && (
        <div style={{ fontSize: "0.72em", opacity: 0.6, fontStyle: "italic", marginBottom: 2 }}>
          ↪ Encaminhada
        </div>
      )}
      {message.isExternal && (
        <div style={{ fontSize: "0.72em", opacity: 0.6, marginBottom: 2 }}>
          📱 Enviado de dispositivo externo
        </div>
      )}
    </>
  );

  const renderDailyTimestamps = (message, index, list) => {
    const src = list || messagesList;
    if (index === 0) {
      return (
        <span
          className={classes.dailyTimestamp}
          key={`timestamp-${message.id}`}
        >
          <div className={classes.dailyTimestampText}>
            {src[index]?.createdAt ? format(parseISO(src[index].createdAt), "dd/MM/yyyy") : ""}
          </div>
        </span>
      );
    }
    if (index < src.length - 1) {
      if (!src[index]?.createdAt || !src[index - 1]?.createdAt) return null;
      let messageDay = parseISO(src[index].createdAt);
      let previousMessageDay = parseISO(src[index - 1].createdAt);

      if (!isSameDay(messageDay, previousMessageDay)) {
        return (
          <span
            className={classes.dailyTimestamp}
            key={`timestamp-${message.id}`}
          >
            <div className={classes.dailyTimestampText}>
              {src[index]?.createdAt ? format(parseISO(src[index].createdAt), "dd/MM/yyyy") : ""}
            </div>
          </span>
        );
      }
    }
    if (index === src.length - 1) {
      return (
        <div
          key={`ref-${message.createdAt}`}
          ref={lastMessageRef}
          style={{ float: "left", clear: "both" }}
        />
      );
    }
  };

  const renderMessageDivider = (message, index, list) => {
    const src = list || messagesList;
    if (index < src.length && index > 0) {
      let messageUser = src[index].fromMe;
      let previousMessageUser = src[index - 1].fromMe;

      if (messageUser !== previousMessageUser) {
        return (
          <span style={{ marginTop: 16 }} key={`divider-${message.id}`}></span>
        );
      }
    }
  };

  const renderQuotedMessage = (message) => {
    const qm = message.quotedMsg;
    const author = qm?.fromMe ? "Você" : (qm?.contact?.name || "Cliente");
    const { icon, text, thumb } = quotedPreview(qm);
    return (
      <div
        className={clsx(classes.quotedContainerLeft, classes.quotedClickable, {
          [classes.quotedContainerRight]: message.fromMe,
        })}
        style={{ alignItems: "center" }}
        onClick={() => scrollToMessage(qm?.id)}
        title="Ir para a mensagem"
      >
        <span
          className={clsx(classes.quotedSideColorLeft, {
            [classes.quotedSideColorRight]: qm?.fromMe,
          })}
        ></span>
        <div className={classes.quotedMsg} style={{ flex: 1, minWidth: 0 }}>
          <span className={classes.messageContactName}>{author}</span>
          <div className={classes.quotedPreviewLine}>
            {icon ? <span style={{ marginRight: 4 }}>{icon}</span> : null}
            {text}
          </div>
        </div>
        {thumb && qm?.mediaUrl ? (
          <QuotedMediaThumb mediaUrl={qm.mediaUrl} size={58} marginLeft={0} />
        ) : null}
      </div>
    );
  };

  const renderMessages = () => {
    const displayList = searchTerm ? filteredMessages : messagesList;
    if (displayList.length > 0) {
      const viewMessagesList = displayList.map((message, index) => {
        if (!message.body && !message.mediaUrl && !message.mediaType && !message.isPrivate) {
          return null;
        }
        if (message.isPrivate) {
          return (
            <React.Fragment key={message.id}>
              {renderDailyTimestamps(message, index)}
              <div className={classes.messagePrivate}>
                <Visibility style={{ fontSize: 13, flexShrink: 0 }} />
                {message.user?.name && (
                  <>
                    <Avatar
                      src={message.user.profileImage || undefined}
                      style={{ width: 20, height: 20, fontSize: 10 }}
                    >
                      {message.user.name[0]}
                    </Avatar>
                    <span className={classes.privateAuthor}>{message.user.name}:</span>
                  </>
                )}
                <span>{message.body}</span>
                <span className={classes.privateTimestamp}>
                  {message.createdAt ? format(parseISO(message.createdAt), "HH:mm") : ""}
                </span>
              </div>
            </React.Fragment>
          );
        }
        if (!message.fromMe) {
          return (
            <React.Fragment key={message.id}>
              {renderDailyTimestamps(message, index)}
              {renderMessageDivider(message, index)}
              <div id={`msg-${message.id}`} className={clsx(classes.messageLeft, { [classes.flashHighlight]: highlightedId === message.id })}>
                {reactionPickerMsgId === message.id && (
                  <div
                    className={`${classes.reactionPickerWrapper} ${classes.reactionPickerWrapperLeft}`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Picker
                      data={emojiData}
                      onEmojiSelect={(emoji) => handleReact(message.id, emoji.native)}
                      locale="pt"
                      previewPosition="none"
                      skinTonePosition="none"
                      theme="auto"
                    />
                  </div>
                )}
                <span
                  className={`${classes.reactionButton} ${classes.reactionButtonLeft}`}
                  onClick={(e) => {
                    // stopPropagation: sem isso o mesmo clique chega ao document
                    // e o listener de "fechar ao clicar fora" fecha o picker na hora
                    e.stopPropagation();
                    setReactionPickerMsgId(reactionPickerMsgId === message.id ? null : message.id);
                  }}
                  title="Reagir"
                >
                  😊
                </span>
                <IconButton
                  variant="contained"
                  size="small"
                  id="messageActionsButton"
                  disabled={message.isDeleted}
                  className={classes.messageActionsButton}
                  style={{ position: "absolute", top: 0, right: 0 }}
                  onClick={(e) => handleOpenMessageOptionsMenu(e, message)}
                >
                  <ExpandMore />
                </IconButton>
                {isGroup && (
                  <span className={classes.messageContactName}>
                    {message.contact?.name}
                  </span>
                )}
                {!message.isDeleted && (message.mediaUrl || message.mediaType === "location" || message.mediaType === "vcard"
                  //|| message.mediaType === "multi_vcard"
                ) && checkMessageMedia(message)}
                <div
                  className={clsx(classes.textContentItem, {
                    [classes.flashHighlight]: highlightedId === message.id,
                    [classes.textContentItemDeleted]: message.isDeleted,
                  })}
                >
                  {message.isDeleted && (
                    <div style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: "0.72em", fontWeight: 700, color: "#d32f2f", marginBottom: 2, textTransform: "uppercase", letterSpacing: 0.3 }}>
                      <Block color="error" style={{ fontSize: 13 }} /> Apagada
                    </div>
                  )}
                  {renderMsgFlags(message)}
                  {message.quotedMsg && renderQuotedMessage(message)}
                  <MarkdownWrapper>
                    {message.body}
                  </MarkdownWrapper>
                  {message.isEdited && !message.isDeleted && (
                    <span style={{ fontSize: "0.72em", opacity: 0.55, marginLeft: 4 }}>✏ editado</span>
                  )}
                  {renderPollVotes(message)}
                  <span className={classes.timestamp}>
                    {message.createdAt ? format(parseISO(message.createdAt), "HH:mm") : ""}
                  </span>
                </div>
                {renderReactions(message)}
              </div>
            </React.Fragment>
          );
        } else {
          return (
            <React.Fragment key={message.id}>
              {renderDailyTimestamps(message, index)}
              {renderMessageDivider(message, index)}
              <div id={`msg-${message.id}`} className={clsx(classes.messageRight, { [classes.flashHighlight]: highlightedId === message.id })}>
                {reactionPickerMsgId === message.id && (
                  <div
                    className={`${classes.reactionPickerWrapper} ${classes.reactionPickerWrapperRight}`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Picker
                      data={emojiData}
                      onEmojiSelect={(emoji) => handleReact(message.id, emoji.native)}
                      locale="pt"
                      previewPosition="none"
                      skinTonePosition="none"
                      theme="auto"
                    />
                  </div>
                )}
                <span
                  className={`${classes.reactionButton} ${classes.reactionButtonRight}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setReactionPickerMsgId(reactionPickerMsgId === message.id ? null : message.id);
                  }}
                  title="Reagir"
                >
                  😊
                </span>
                <IconButton
                  variant="contained"
                  size="small"
                  id="messageActionsButton"
                  disabled={message.isDeleted}
                  className={classes.messageActionsButton}
                  style={{ position: "absolute", top: 0, right: 0 }}
                  onClick={(e) => handleOpenMessageOptionsMenu(e, message)}
                >
                  <ExpandMore />
                </IconButton>
                {!message.isDeleted && (message.mediaUrl || message.mediaType === "location" || message.mediaType === "vcard"
                  //|| message.mediaType === "multi_vcard"
                ) && checkMessageMedia(message)}
                <div
                  className={clsx(classes.textContentItem, {
                    [classes.flashHighlight]: highlightedId === message.id,
                    [classes.textContentItemDeleted]: message.isDeleted,
                  })}
                >
                  {message.isDeleted && (
                    <div style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: "0.72em", fontWeight: 700, color: "#d32f2f", marginBottom: 2, textTransform: "uppercase", letterSpacing: 0.3 }}>
                      <Block color="error" style={{ fontSize: 13 }} /> Apagada
                    </div>
                  )}
                  {renderMsgFlags(message)}
                  {message.quotedMsg && renderQuotedMessage(message)}
                  <MarkdownWrapper>
                    {message.body}
                  </MarkdownWrapper>
                  {message.isEdited && !message.isDeleted && (
                    <span style={{ fontSize: "0.72em", opacity: 0.55, marginLeft: 4 }}>✏ editado</span>
                  )}
                  {renderPollVotes(message)}
                  <span className={classes.timestamp}>
                    {message.createdAt ? format(parseISO(message.createdAt), "HH:mm") : ""}
                    {renderMessageAck(message)}
                  </span>
                </div>
                {renderReactions(message)}
              </div>
            </React.Fragment>
          );
        }
      });
      return viewMessagesList;
    } else {
      return <div>Say hello to your new contact!</div>;
    }
  };

  const highlightText = (text, term) => {
    if (!term || !text) return text;
    const parts = text.split(new RegExp(`(${term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "gi"));
    return parts.map((part, i) =>
      part.toLowerCase() === term.toLowerCase()
        ? <mark key={i} className={classes.searchHighlight}>{part}</mark>
        : part
    );
  };

  const filteredMessages = searchTerm
    ? messagesList.filter((m) => m.body && m.body.toLowerCase().includes(searchTerm.toLowerCase()))
    : messagesList;

  return (
    <div className={classes.messagesListWrapper}>
      <MessageOptionsMenu
        message={selectedMessage}
        anchorEl={anchorEl}
        menuOpen={messageOptionsMenuOpen}
        handleClose={handleCloseMessageOptionsMenu}
      />
      {pdfView && (
        <PdfViewer
          blobUrl={pdfView.blobUrl}
          filename={pdfView.filename}
          loading={pdfView.loading}
          onClose={closePdfView}
        />
      )}
      <Dialog open={!!reactionsDetail} onClose={() => setReactionsDetail(null)} maxWidth="xs" fullWidth>
        <DialogTitle style={{ fontSize: 16 }}>Reações</DialogTitle>
        <DialogContent dividers>
          {reactionsDetail && Object.entries(reactionsDetail.reactions).map(([emoji, senders]) => (
            <div key={emoji} style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "6px 0", borderBottom: "1px solid #f0f0f0" }}>
              <span style={{ fontSize: 24, lineHeight: 1 }}>{emoji}</span>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                {(Array.isArray(senders) ? senders : []).map((s, i) => (
                  <span key={i} style={{ fontSize: 13, background: "#f0f4ff", border: "1px solid #d0d9f5", borderRadius: 10, padding: "1px 8px" }}>
                    {reactionSenderLabel(s)}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </DialogContent>
      </Dialog>
      {searchOpen && (
        <div className={classes.searchBar}>
          <TextField
            inputRef={searchRef}
            size="small"
            placeholder="Buscar mensagens..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            autoFocus
            fullWidth
            variant="outlined"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search style={{ fontSize: 18, color: "inherit" }} />
                </InputAdornment>
              ),
              endAdornment: searchTerm ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchTerm("")}>
                    <Close style={{ fontSize: 16 }} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
            inputProps={{ style: { fontSize: 13, padding: "6px 8px" } }}
          />
          {searchTerm && (
            <Typography variant="caption" style={{ whiteSpace: "nowrap", color: "inherit" }}>
              {filteredMessages.length} resultado{filteredMessages.length !== 1 ? "s" : ""}
            </Typography>
          )}
          <IconButton size="small" onClick={() => { setSearchOpen(false); setSearchTerm(""); }}>
            <Close style={{ fontSize: 18 }} />
          </IconButton>
        </div>
      )}
      <div
        id="messagesList"
        ref={messagesListRef}
        className={classes.messagesList}
        onScroll={handleScroll}
        style={
          bg?.type === "color"
            ? { backgroundColor: bg.value, backgroundImage: "none" }
            : bg?.type === "image"
            ? { backgroundImage: `url(${bg.value})`, backgroundSize: "cover", backgroundPosition: "center", backgroundAttachment: "local" }
            : {}
        }
      >
        <div className={classes.syncHistoryBar}>
          {syncStatus === "idle" && (
            <Button
              size="small"
              startIcon={<History fontSize="small" />}
              className={classes.syncHistoryButton}
              onClick={handleSyncHistory}
            >
              Localizar histórico
            </Button>
          )}
          {(syncStatus === "fetching" || syncStatus === "syncing") && (
            <div className={classes.syncProgressWrap}>
              <LinearProgress
                style={{ width: "100%" }}
                variant={syncStatus === "fetching" ? "indeterminate" : "determinate"}
                value={
                  syncStatus === "syncing" && syncProgress.total > 0
                    ? Math.round((syncProgress.current / syncProgress.total) * 100)
                    : 0
                }
              />
              <Typography className={classes.syncProgressText}>
                {syncStatus === "fetching"
                  ? "Buscando mensagens no WhatsApp..."
                  : `Salvando ${syncProgress.current} de ${syncProgress.total} mensagens...`}
              </Typography>
            </div>
          )}
          {syncStatus === "done" && (
            <Typography className={classes.syncDoneText}>
              ✓ Histórico carregado com sucesso
            </Typography>
          )}
          {syncStatus === "error" && (
            <Button
              size="small"
              startIcon={<History fontSize="small" />}
              className={classes.syncErrorButton}
              onClick={handleSyncHistory}
            >
              Erro ao buscar — tentar novamente
            </Button>
          )}
        </div>

        {hasMore && (
          <Button
            variant="outlined"
            size="small"
            className={classes.loadMoreButton}
            onClick={loadMore}
            disabled={loading}
          >
            {loading ? <CircularProgress size={16} style={{ marginRight: 6 }} /> : null}
            Mostrar mensagens anteriores
          </Button>
        )}
        {messagesList.length > 0 ? renderMessages() : []}
      </div>
      {loading && pageNumber === 1 && (
        <div>
          <CircularProgress className={classes.circleLoading} />
        </div>
      )}
      {showScrollBtn && (
        <Tooltip title="Ir para o final">
          <IconButton
            onClick={() => scrollToBottom(true)}
            style={{
              position: "absolute",
              bottom: 14,
              right: 14,
              zIndex: 10,
              width: 32,
              height: 32,
              minWidth: 0,
              padding: 0,
              backgroundColor: "#fff",
              border: "1px solid rgba(0,0,0,0.15)",
              borderRadius: "50%",
              boxShadow: "0 2px 6px rgba(0,0,0,0.22)",
            }}
          >
            <KeyboardArrowDown style={{ fontSize: 20 }} />
          </IconButton>
        </Tooltip>
      )}
    </div>
  );
};

export default MessagesList;